import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Direct Sale Lines behind a set of Dir Sale Line Prog Prod matches. A Direct Sale Line is only
 * reachable through those match records, so the matched ids are resolved first and then used to filter
 * the Sale Lines; the ids become bind parameters and Postgres caps a statement at 65,535 of them.
 *
 * Single choke point for every "which sales rolled into this?" tab - callers supply the match expr.
 */
export const MatchedDirSaleLines = ({ matchExpr }) => {
  const rsrc = useRsrc();
  const entityListRef = useRef();
  const [dirSaleLineIds, setDirSaleLineIds] = useState(null);

  useEffect(() => {
    let active = true;

    rsrc.rows('sc', 'DIR_SALE_LINE_PROG_PROD', { expr: matchExpr }).then((rows) => {
      if (active) setDirSaleLineIds(rows.map((row) => row.dirSaleLineId));
    });

    return () => {
      active = false;
    };
  }, [matchExpr, rsrc]);

  const totalsRow = () => {
    const gridRef = entityListRef.current.gridRef;

    const totals = { code: 'TOTAL', salesAmt: 0, netSalesAmt: 0, salesQty: 0 };

    const api = gridRef.current.gridApiRef.current;
    api.forEachNodeAfterFilterAndSort((node) => {
      totals.salesAmt = totals.salesAmt + (node.data.salesAmt ?? 0);
      totals.netSalesAmt = totals.netSalesAmt + (node.data.netSalesAmt ?? 0);
      totals.salesQty = totals.salesQty + (node.data.salesQty ?? 0);
    });
    totals.salesAmt = Math.round(totals.salesAmt * 100) / 100;
    totals.netSalesAmt = Math.round(totals.netSalesAmt * 100) / 100;
    totals.salesQty = Math.round(totals.salesQty * 1000) / 1000;

    gridRef.current.setTotalsRow(totals);
  };

  // Client side so that the totals cover every matched Sale Line rather than the scrolled page
  const clientSideRowData = useCallback(
    (setDataSource) => {
      if (dirSaleLineIds.length === 0) {
        setDataSource([]);
        totalsRow();
        return;
      }

      rsrc.rows('sc', 'DIR_SALE_LINE', { expr: ExprUtils.setExpr('id', dirSaleLineIds) }).then((rows) => {
        setDataSource(rows);
        totalsRow();
      });
    },
    [dirSaleLineIds, rsrc],
  );

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
  };

  if (dirSaleLineIds == null) return null;

  return (
    <EntityList
      ref={entityListRef}
      module="sc"
      entity="DIR_SALE_LINE"
      clientSideRowData={clientSideRowData}
      augTabButtons={augTabButtons}
      events={{ onFilterChanged: totalsRow }}
    />
  );
};
